import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, Sprout, TrendingUp, Target, Zap, Link2, Compass, ListChecks,
  MessageCircle, CalendarDays, HandHeart, HelpCircle, Quote, Lock, Download, ShieldCheck, Ticket,
} from "lucide-react";
import { GFFrame } from "@/components/GFFrame";
import {
  getPersonalAssessment, startPersonalPayment, getPersonalPaymentStatus, downloadPersonalPdf, downloadPersonalSnapshot, redeemPersonalMentorCode,
} from "@/lib/gfApi";

const EMERALD = "#064E3B";
const GOLD = "#D4AF37";
const FACTOR_ORDER = ["kesedaran", "daya_tahan", "hala_tuju", "pelaksanaan", "hubungan"];
const TONE = {
  strength: "bg-emerald-100 text-emerald-700",
  potensi: "bg-teal-100 text-teal-700",
  ruang: "bg-amber-100 text-amber-700",
  priority: "bg-orange-100 text-orange-700",
};

const Gauge = ({ score }) => {
  const size = 180, stroke = 13, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, score)) / 100) * c;
  return (
    <svg width={size} height={size} className="mx-auto block" data-testid="pg-gauge">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E3EDE8" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={GOLD} strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: "stroke-dashoffset 1s ease" }} />
      <text x="50%" y="47%" textAnchor="middle" dominantBaseline="middle" className="font-heading" fontSize={size * 0.3} fontWeight="700" fill={EMERALD}>{score}</text>
      <text x="50%" y="66%" textAnchor="middle" fontSize={size * 0.07} fontWeight="600" fill="#7C9C8E" letterSpacing="2">/ 100</text>
    </svg>
  );
};

const Bar = ({ score }) => (
  <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#E3EDE8]">
    <div className="h-full rounded-full" style={{ width: `${score}%`, background: EMERALD, transition: "width 0.9s ease" }} />
  </div>
);

const Section = ({ n, title, icon: Icon, children, testId }) => (
  <section className="mt-8 px-6" data-testid={testId}>
    <div className="mb-4">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Bahagian {n}</p>
      <div className="mt-1 flex items-center gap-2">
        {Icon ? <Icon className="h-5 w-5" style={{ color: EMERALD }} /> : <span className="h-5 w-1 rounded-full" style={{ background: GOLD }} />}
        <h3 className="font-heading text-xl font-bold" style={{ color: EMERALD }}>{title}</h3>
      </div>
    </div>
    {children}
  </section>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-[#DCE7E1] bg-white p-5 ${className}`}>{children}</div>
);

export default function PersonalReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [paying, setPaying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [checkout, setCheckout] = useState({ email: "", phone: "" });
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const load = () => getPersonalAssessment(id).then(setD).catch(() => setErr(true));

  const redeem = async () => {
    if (!code.trim()) {
      toast.error("Sila masukkan kod program.");
      return;
    }
    setRedeeming(true);
    try {
      await redeemPersonalMentorCode(id, code.trim(), checkout.email || "");
      toast.success("Kod program disahkan. Laporan penuh dibuka!");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod program tidak sah.");
    } finally {
      setRedeeming(false);
    }
  };

  useEffect(() => {
    (async () => {
      await getPersonalPaymentStatus(id).catch(() => {});
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const pay = async () => {
    if (!checkout.email || !checkout.phone) {
      toast.error("Sila masukkan emel dan nombor telefon.");
      return;
    }
    setPaying(true);
    try {
      const res = await startPersonalPayment(id, checkout);
      if (res.already_paid) { window.location.reload(); return; }
      if (res.payment_url) { window.location.assign(res.payment_url); }
      else throw new Error("no url");
    } catch (e) {
      setPaying(false);
      const msg = e?.response?.status === 503
        ? "Pembayaran belum dikonfigurasi. Sila hubungi pentadbir."
        : "Gagal memulakan pembayaran. Sila cuba lagi.";
      toast.error(msg);
    }
  };

  const doDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadPersonalPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-personal-growth-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Laporan PDF dimuat turun.");
    } catch (e) {
      toast.error("Gagal memuat turun laporan. Sila cuba lagi.");
    } finally {
      setDownloading(false);
    }
  };

  const doDownloadSnapshot = async () => {
    setSnapshotting(true);
    try {
      const blob = await downloadPersonalSnapshot(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `growth-snapshot-personal-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("A4 Growth Snapshot dimuat turun.");
    } catch (e) {
      toast.error("Gagal memuat turun snapshot. Sila cuba lagi.");
    } finally {
      setSnapshotting(false);
    }
  };

  if (err) return <GFFrame><div className="flex min-h-screen items-center justify-center text-slate-400">Laporan tidak dijumpai.</div></GFFrame>;
  if (!d) return (
    <GFFrame>
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: GOLD }} /><span className="text-sm">Memuatkan laporan…</span>
      </div>
    </GFFrame>
  );

  // ---- FREE PREVIEW + RM15 PAYWALL (before payment) ----
  if (!d.paid) {
    const inputCls = "w-full rounded-2xl border border-[#DCE7E1] bg-white px-4 py-3.5 text-[15px] text-[#064E3B] outline-none focus:border-[#D4AF37]";
    const locked = [
      "Tafsiran mendalam corak dalaman diri",
      "Analisis penuh 5 faktor + bukti item",
      "Kekuatan teras & keutamaan pertumbuhan",
      "Geseran tersembunyi & zon peluang",
      "Preskripsi praktikal & Pelan 30 Hari",
    ];
    return (
      <GFFrame testId="pg-report-preview-page">
        <div className="min-h-screen pb-16">
          <div className="px-6 pb-8 pt-10 text-white" style={{ background: EMERALD }}>
            <div className="flex items-center gap-2">
              <Sprout className="h-4 w-4" style={{ color: GOLD }} />
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Personal Growth</span>
            </div>
            <h1 className="mt-3 font-heading text-3xl font-bold" style={{ color: GOLD }}>Keputusan Ringkas {d.preview?.name || (d.user?.nama || "").split(" ")[0]}</h1>
            <p className="mt-1 text-sm text-[#A7C7B8]">{d.user.nama}</p>
          </div>

          {/* Free: overall + 5 factor scores only */}
          <section className="mt-8 px-6" data-testid="pg-preview-scores">
            <div className="text-center">
              <Gauge score={d.overall} />
              <div className="mx-auto mt-3 inline-block rounded-full px-5 py-2 text-sm font-bold uppercase tracking-wider text-white" style={{ background: EMERALD }}>
                {d.category}
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {FACTOR_ORDER.map((k) => (
                <div key={k}>
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-sm font-semibold" style={{ color: EMERALD }}>{d.factor_labels[k]}</span>
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">{d.scores[k]}%</span>
                  </div>
                  <Bar score={d.scores[k]} />
                </div>
              ))}
            </div>
          </section>

          {/* Conversion preview: gemstone + strengths + observation + palette teaser */}
          {d.preview && (
            <section className="mt-8 px-6" data-testid="pg-preview-narrative">
              <p className="text-[15px] leading-relaxed text-slate-700">{d.preview.opening}</p>

              <div className="mt-5 rounded-2xl p-5 text-white" style={{ background: EMERALD }} data-testid="pg-preview-gemstone">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Permata {d.preview.name}</span>
                <h3 className="mt-1 font-heading text-2xl font-bold" style={{ color: GOLD }}>{d.preview.gemstone.name}</h3>
                <p className="mt-2 text-sm text-[#C9DBD0]">{d.preview.gemstone.why}</p>
              </div>

              <div className="mt-5">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#B8912B" }}>3 Kekuatan Utama</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {d.preview.strengths.map((s, i) => (
                    <span key={i} className="rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-semibold" style={{ color: EMERALD }}>{s}</span>
                  ))}
                </div>
              </div>

              <p className="mt-5 rounded-xl border-l-4 bg-[#FBFAF6] p-4 text-sm italic leading-relaxed text-slate-600" style={{ borderColor: GOLD }}>
                {d.preview.observation}
              </p>

              <div className="mt-5">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "#B8912B" }}>Cebisan Palet Aura</span>
                <div className="mt-2 flex gap-3">
                  {d.preview.palette_preview.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="h-6 w-6 rounded-full border border-slate-200" style={{ background: c.hex }} />
                      <span className="text-xs text-slate-500">{c.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Snapshot preview teaser */}
          <section className="mt-8 px-6" data-testid="pg-snapshot-preview-section">
            <div className="rounded-2xl border p-4" style={{ borderColor: "#DCE7E1", background: "#fff" }}>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: "#B8912B" }}>Termasuk Percuma</p>
              <h3 className="mt-1 font-heading text-lg font-bold" style={{ color: EMERALD }}>A4 Growth Snapshot — inilah rupanya</h3>
              <p className="mt-1 text-sm text-slate-500">Ringkasan satu halaman yang boleh dicetak sebagai peringatan harian. Buka penuh selepas RM15.</p>
              <div className="mt-4 overflow-hidden rounded-xl border" style={{ height: 420, borderColor: "#DCE7E1", pointerEvents: "none" }}>
                <iframe
                  title="A4 Personal Growth Snapshot Preview"
                  data-testid="pg-snapshot-preview-frame"
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/personal/assessments/${id}/snapshot_preview.pdf#toolbar=0&navpanes=0&view=FitH`}
                  style={{ width: "100%", height: "100%", border: 0 }}
                />
              </div>
            </div>
          </section>

          {/* Paywall */}
          <section className="mt-8 px-6" data-testid="pg-paywall-card">
            <div className="rounded-2xl border p-5" style={{ borderColor: GOLD, background: "#FFFBEF" }}>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5" style={{ color: "#B8912B" }} />
                <span className="font-heading text-lg font-bold" style={{ color: EMERALD }}>Buka Laporan Penuh — RM15</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Laporan penuh mengandungi tafsiran terperinci hasil ini:
              </p>
              <ul className="mt-3 space-y-2">
                {locked.map((t, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-slate-600">
                    <Lock className="mt-0.5 h-4 w-4 shrink-0" style={{ color: GOLD }} />{t}
                  </li>
                ))}
              </ul>

              <div className="mt-5 space-y-3">
                <input data-testid="pg-checkout-email" type="email" className={inputCls} placeholder="emel@contoh.com"
                  value={checkout.email} onChange={(e) => setCheckout({ ...checkout, email: e.target.value })} />
                <input data-testid="pg-checkout-phone" className={inputCls} placeholder="0123456789"
                  value={checkout.phone} onChange={(e) => setCheckout({ ...checkout, phone: e.target.value })} />
                <button data-testid="pg-pay-toyyibpay-btn" onClick={pay} disabled={paying}
                  className="flex w-full items-center justify-center gap-2 rounded-full py-4 text-base font-bold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
                  style={{ background: EMERALD }}>
                  {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : null} BAYAR RM15 & BUKA LAPORAN
                </button>
                <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                  <ShieldCheck className="h-3.5 w-3.5" /> Pembayaran selamat melalui ToyyibPay
                </p>
              </div>

              {/* Mentor / Program Access Code bypass */}
              <div className="mt-5 border-t border-dashed border-[#DCE7E1] pt-4" data-testid="pg-mentor-code-block">
                {!showCode ? (
                  <button
                    data-testid="pg-have-program-code-btn"
                    onClick={() => setShowCode(true)}
                    className="flex w-full items-center justify-center gap-2 text-sm font-semibold underline-offset-4 hover:underline"
                    style={{ color: EMERALD }}
                  >
                    <Ticket className="h-4 w-4" style={{ color: "#B8912B" }} /> Ada Kod Program?
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#B8912B" }}>Kod Program Mentor</p>
                    <p className="text-sm text-slate-500">Peserta program mentor/latihan boleh membuka laporan penuh tanpa bayaran menggunakan kod yang diberikan.</p>
                    <input
                      data-testid="pg-mentor-code-input"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && redeem()}
                      placeholder="cth: MENTOR-AB12CD"
                      className={`${inputCls} font-bold uppercase tracking-widest`}
                    />
                    <button
                      data-testid="pg-redeem-mentor-code-btn"
                      onClick={redeem}
                      disabled={redeeming}
                      className="flex w-full items-center justify-center gap-2 rounded-full border-2 py-3 text-sm font-bold transition-transform active:scale-[0.98] disabled:opacity-60"
                      style={{ borderColor: EMERALD, color: EMERALD }}
                    >
                      {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />} SAHKAN KOD & BUKA LAPORAN
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>

          <div className="mt-8 px-6 text-center">
            <button onClick={() => navigate("/")} data-testid="pg-preview-home-btn"
              className="rounded-full border border-[#DCE7E1] px-6 py-2.5 text-sm font-semibold" style={{ color: EMERALD }}>
              Kembali ke Laman Utama
            </button>
          </div>
        </div>
      </GFFrame>
    );
  }

  const r = d.full_report;

  return (
    <GFFrame testId="pg-report-page">
      <div className="min-h-screen pb-16">
        {/* Header + Snapshot */}
        <div className="px-6 pb-8 pt-10 text-white" style={{ background: EMERALD }}>
          <div className="flex items-center gap-2">
            <Sprout className="h-4 w-4" style={{ color: GOLD }} />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Personal Growth</span>
          </div>
          <h1 className="mt-3 font-heading text-3xl font-bold" style={{ color: GOLD }}>Laporan Pertumbuhan Diri</h1>
          <p className="mt-1 text-sm text-[#A7C7B8]">{d.user.nama}</p>
          <button
            data-testid="pg-download-report-btn"
            onClick={doDownload}
            disabled={downloading}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold transition-transform active:scale-[0.98] disabled:opacity-60"
            style={{ background: GOLD, color: EMERALD }}
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Muat Turun Laporan
          </button>
          <button
            data-testid="pg-download-snapshot-btn"
            onClick={doDownloadSnapshot}
            disabled={snapshotting}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 py-3 text-sm font-bold transition-colors disabled:opacity-60"
            style={{ borderColor: GOLD, color: GOLD }}
          >
            {snapshotting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download A4 Growth Snapshot
          </button>
        </div>

        {/* 1 — Snapshot */}
        <Section n="1" title="Gambaran Pertumbuhan" icon={TrendingUp} testId="pg-snapshot">
          <div className="text-center">
            <Gauge score={r.snapshot.overall} />
            <div className="mx-auto mt-3 inline-block rounded-full px-5 py-2 text-sm font-bold uppercase tracking-wider text-white" style={{ background: EMERALD }}>
              {r.snapshot.category}
            </div>
          </div>
          <div className="mt-6 space-y-4">
            {r.snapshot.factors.map((f) => (
              <div key={f.key}>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-sm font-semibold" style={{ color: EMERALD }}>{f.label}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE[f.tone]}`}>{f.score}%</span>
                </div>
                <Bar score={f.score} />
              </div>
            ))}
          </div>
        </Section>

        {/* 2 — Where you are now */}
        <Section n="2" title="Di Mana Perjalanan Ini Berada" icon={Compass} testId="pg-where-now">
          <div className="space-y-3">
            {r.where_now.paragraphs.map((p, i) => (<p key={i} className="text-sm leading-relaxed text-slate-600">{p}</p>))}
          </div>
        </Section>

        {/* 3 — Five factors */}
        <Section n="3" title="Lima Faktor Pertumbuhan" icon={ListChecks} testId="pg-factors">
          <div className="space-y-4">
            {r.factors.map((f) => (
              <Card key={f.key}>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-base font-bold" style={{ color: EMERALD }}>{f.label}</span>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${TONE[f.tone]}`}>{f.status}</span>
                </div>
                <div className="mt-2"><Bar score={f.score} /></div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.suggests}</p>
                <p className="mt-2 text-sm italic leading-relaxed text-slate-500">{f.evidence}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600"><span className="font-semibold" style={{ color: EMERALD }}>Dalam kehidupan harian: </span>{f.daily}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600"><span className="font-semibold" style={{ color: EMERALD }}>Kenapa ia penting: </span>{f.why_matters}</p>
              </Card>
            ))}
          </div>
        </Section>

        {/* 4 — Core strength */}
        <Section n="4" title="Kekuatan Teras" icon={Zap} testId="pg-strength">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              <span className="font-heading text-lg font-bold" style={{ color: EMERALD }}>{r.core_strength.label}</span>
              <span className="text-sm font-bold text-emerald-600">{r.core_strength.score}%</span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.core_strength.text}</p>
            <ul className="mt-3 space-y-2">
              {r.core_strength.how_to_use.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-slate-600"><Zap className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{s}</li>
              ))}
            </ul>
          </div>
        </Section>

        {/* 5 — Growth priority */}
        <Section n="5" title="Keutamaan Pertumbuhan" icon={Target} testId="pg-priority">
          <div className="rounded-2xl border p-5" style={{ borderColor: GOLD, background: "#FFFBEF" }}>
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5" style={{ color: "#B8912B" }} />
              <span className="font-heading text-lg font-bold" style={{ color: EMERALD }}>{r.growth_priority.label}</span>
              <span className="text-sm font-bold" style={{ color: "#B8912B" }}>{r.growth_priority.score}%</span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.growth_priority.why}</p>
            <p className="mt-2 text-sm font-medium leading-relaxed" style={{ color: EMERALD }}>{r.growth_priority.note}</p>
          </div>
        </Section>

        {/* 6 — Hidden friction */}
        <Section n="6" title="Geseran Tersembunyi" icon={Link2} testId="pg-friction">
          <div className="space-y-3">
            {r.friction.map((f, i) => (
              <div key={i} className="flex gap-3 rounded-2xl border border-[#DCE7E1] bg-white p-4">
                <Link2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: GOLD }} />
                <p className="text-sm leading-relaxed text-slate-600">{f}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* 7 — Opportunity zone */}
        <Section n="7" title="Zon Peluang" icon={Compass} testId="pg-opportunity">
          <div className="rounded-2xl p-5 text-white" style={{ background: EMERALD }}>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Titik Tuas: {r.opportunity.focus}</p>
            <p className="mt-2 text-sm leading-relaxed text-[#D6E7DF]">{r.opportunity.text}</p>
          </div>
        </Section>

        {/* 8 — Practical prescription */}
        <Section n="8" title="Preskripsi Pertumbuhan Praktikal" icon={ListChecks} testId="pg-prescription">
          <div className="space-y-3">
            {r.prescription.map((p, i) => (
              <Card key={i}>
                <p className="font-heading text-base font-bold" style={{ color: EMERALD }}>{p.what}</p>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  <p><span className="font-semibold" style={{ color: EMERALD }}>Bagaimana: </span>{p.how}</p>
                  <p><span className="font-semibold" style={{ color: EMERALD }}>Bila: </span>{p.when}</p>
                  <p><span className="font-semibold" style={{ color: EMERALD }}>Tanda berjaya: </span>{p.success}</p>
                </div>
              </Card>
            ))}
          </div>
        </Section>

        {/* 9 — Communication */}
        <Section n="9" title="Pertumbuhan Komunikasi & Hubungan" icon={MessageCircle} testId="pg-communication">
          <Card>
            <p className="text-sm leading-relaxed text-slate-600">{r.communication.intro}</p>
            <ul className="mt-3 space-y-2">
              {r.communication.recs.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-slate-600"><MessageCircle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: GOLD }} />{s}</li>
              ))}
            </ul>
          </Card>
        </Section>

        {/* 10 — 30-day */}
        <Section n="10" title="Preskripsi Pertumbuhan 30 Hari" icon={CalendarDays} testId="pg-thirty-day">
          <div className="space-y-4">
            {r.thirty_day.map((wk, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }} className="rounded-2xl border border-[#DCE7E1] bg-white p-5">
                <span className="font-heading text-base font-bold" style={{ color: EMERALD }}>{wk.title}</span>
                <p className="mt-1 text-sm font-medium" style={{ color: "#B8912B" }}>{wk.objective}</p>
                <ul className="mt-3 space-y-2">
                  {wk.actions.map((a, j) => (
                    <li key={j} className="flex gap-3 text-sm text-slate-600">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ background: EMERALD }}>{j + 1}</span>{a}
                    </li>
                  ))}
                </ul>
                <div className="mt-3 grid gap-1.5 rounded-xl p-3 text-xs text-slate-600" style={{ background: "#F1F6F3" }}>
                  <p><span className="font-semibold" style={{ color: EMERALD }}>Kekerapan: </span>{wk.frequency}</p>
                  <p><span className="font-semibold" style={{ color: EMERALD }}>Bukti: </span>{wk.metric}</p>
                  <p className="italic"><span className="font-semibold not-italic" style={{ color: EMERALD }}>Renungan: </span>{wk.reflection}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>

        {/* 10b — 3-6 month roadmap (blueprint — mirrors A4 & PDF) */}
        {r.roadmap_6month?.length > 0 && (
          <Section n="10b" title="Pelan Pertumbuhan 3–6 Bulan" icon={CalendarDays} testId="pg-roadmap">
            <div className="space-y-3">
              {r.roadmap_6month.map((m, i) => (
                <div key={i} className="rounded-2xl border border-[#DCE7E1] bg-white p-4" data-testid={`pg-roadmap-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ background: EMERALD }}>{i + 1}</span>
                    <p className="font-heading text-sm font-bold" style={{ color: EMERALD }}>{m.title}</p>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.focus}</p>
                  <p className="mt-1.5 text-xs font-semibold" style={{ color: "#B8912B" }}>{m.note}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* 11 — Commitments */}
        <Section n="11" title="Komitmen Peribadi" icon={HandHeart} testId="pg-commitments">
          <div className="space-y-2.5">
            {r.commitments.map((c, i) => (
              <div key={i} className="flex gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4">
                <HandHeart className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <p className="text-sm font-medium" style={{ color: EMERALD }}>{c}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* 12 — Reflection */}
        <Section n="12" title="Soalan Renungan" icon={HelpCircle} testId="pg-reflection">
          <div className="space-y-2.5">
            {r.reflection.map((q, i) => (
              <div key={i} className="rounded-2xl border border-[#DCE7E1] bg-white px-4 py-3 text-sm italic text-slate-600">“{q}”</div>
            ))}
          </div>
        </Section>

        {/* 13 — Closing */}
        <Section n="13" title="Nota Penutup" icon={Quote} testId="pg-closing">
          <div className="rounded-2xl p-6 text-white" style={{ background: EMERALD }}>
            <Quote className="h-5 w-5" style={{ color: GOLD }} />
            <div className="mt-3 space-y-3">
              {r.closing.map((p, i) => (<p key={i} className="text-sm leading-relaxed text-[#D6E7DF]">{p}</p>))}
            </div>
          </div>
        </Section>

        <div className="mt-10 px-6 text-center">
          <p className="text-xs text-slate-400">Laporan ini adalah panduan reflektif untuk pembangunan diri, bukan diagnosis perubatan atau psikologi.</p>
          <button onClick={() => navigate("/")} data-testid="pg-home-btn"
            className="mt-4 rounded-full border border-[#DCE7E1] px-6 py-2.5 text-sm font-semibold" style={{ color: EMERALD }}>
            Kembali ke Laman Utama
          </button>
        </div>
      </div>
    </GFFrame>
  );
}
